import React, { useEffect, useState } from 'react';
import useApp from '../../../utils/useApp';
import { Button, Col, Container, Row, Spinner, Stack } from 'react-bootstrap';
import { fetchAllResources, fetchResourceTypes, postResource } from '../../../services/httpClient';
import { Resource } from '../../../types/resource';
import { unsavedChanges } from '../../../App';
import SaveChangesModal from '../../ui/SaveChangesModal';
import { faAdd } from '@fortawesome/free-solid-svg-icons';
import OrgResourceCard from './OrgResourceCard';
import IconEmbeddedButton from '../../ui/IconEmbeddedButton';
import SelectObject from '../../ui/SelectObject';
import Input from '../../ui/Input';

interface OrgResourcesState {
  setOrgToSave: any;
  getData: boolean;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const OrgResources = ({
  setOrgToSave,
  getData,
  setShowToast,
  setToastText,
  setToastBody,
}: OrgResourcesState) => {
  const { auth, showMenu }: any = useApp();
  const [showAddResource, setShowAddResource] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [resources, setResources] = useState<Resource[]>([]);
  const [consumables, setConsumables] = useState<Resource[]>([]);
  const [nonConsumables, setNonConsumables] = useState<Resource[]>([]);
  const [resourceTypes, setResourceTypes] = useState<any[]>([]);
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [allowSaveResource, setAllowSaveResource] = useState<boolean>(false);
  const [disableType, setDisableType] = useState<boolean>(false);
  const [resourceType, setResourceType] = useState<string>("0");
  const [resourceName, setResourceName] = useState<string>("");
  const [resourceQuantity, setResourceQuantity] = useState<number>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errMsg, setErrMsg] = useState<string>("");

  const showConsumableSection = typeFilter === "all" || typeFilter === "consumable";
  const showNonConsumableSection = typeFilter === "all" || typeFilter === "nonconsumable";

  useEffect(() => {
    if (getData) {
      getResources();
      getResourceTypes();
    }
  }, [getData]);

  useEffect(() => {
    getResources();
    getResourceTypes();
  }, [auth?.profile]);

  useEffect(() => {
    const validInput = (
      resourceType !== "0" &&
      resourceName?.trim() &&
      resourceQuantity
    );

    validInput
      ? setAllowSaveResource(true)
      : setAllowSaveResource(false);
  }, [resourceType, resourceName, resourceQuantity]);

  const getResources = () => {
    setIsLoading(true);
    fetchAllResources()
      .then((res: any) => {
        const resources = res[0].data;
        setResources(resources);
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getResourceTypes = () => {
    setIsLoading(true);
    fetchResourceTypes()
      .then((res: any) => {
        const types = res[0].data;
        setResourceTypes(types);
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const onClickSaveResource = (isDone: boolean = false) => {
    setErrMsg("");

    const newResource = {
      type_key: resourceType,
      name: resourceName,
      quantity: resourceQuantity,
    };

    postResource(newResource)
      .then((res: any) => {
        setShowToast(true);
        setToastText("Resource Created");
        setToastBody(resourceName);
        setResourceName("");
        setResourceQuantity(undefined);
        unsavedChanges.value = false;
        setAllowSaveResource(false);
        setOrgToSave(false);
        if (isDone) {
          setShowAddResource(false);
          setResourceType("0");
        }
        getResources();
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickAddResource = (type?: string) => {
    setErrMsg("");
    setShowAddResource(true);
    if (type) {
      setDisableType(true);
      setResourceType(type);
    } else {
      setDisableType(false);
    }
  };

  const onClickAddResourceCancel = () => {
    unsavedChanges.value = false;
    setErrMsg("");
    setShowAddResource(false);
    setResourceType("0");
    setResourceName("");
    setResourceQuantity(undefined);
    setOrgToSave(false);
    getResources();
  };

  const updateName = (e: any) => {
    setResourceName(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const updateQuantity = (e: any) => {
    setResourceQuantity(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  useEffect(() => {
    applyFilters(typeFilter);
  }, [resources]);

  const applyFilters = (type: string) => {
    let filteredResources = [...resources];

    if (type !== "all") {
      filteredResources = filteredResources.filter((r) => r.type_key === type);
    }

    const consumables = filteredResources.filter((r: any) => r.type_key === "consumable");
    const nonConsumables = filteredResources.filter((r: any) => r.type_key === "nonconsumable");

    setConsumables(consumables);
    setNonConsumables(nonConsumables);
  };
  
  const onClickFilterType = (type: string) => {
    setTypeFilter(type);
    applyFilters(type);
    setShowAddResource(false);
  };

  return (
    <>
      {isLoading ? (
        <Container className={showMenu ? "lg div-main-menu" : "lg div-main"}>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <Row>
            <Col xs={3}>
              <h4 className="app-header-text">Resources</h4>
            </Col>
          </Row>
          {resources.length === 0 ? (
            <>
              <p className="mt-2">No Current Resources</p>
            </>
          ) : (
            <>
              <Row>
                <Col className="d-flex justify-content-center gap-3">
                  <Button
                    className={typeFilter === "all" ? "app-primary-color button" : "app-primary-color-inverse button"}
                    onClick={() => onClickFilterType("all")}
                  >
                    View All
                  </Button>
                  <Button
                    className={typeFilter === "consumable" ? "app-primary-color button" : "app-primary-color-inverse button"}
                    onClick={() => onClickFilterType("consumable")}
                  >
                    Consumable
                  </Button>
                  <Button
                    className={typeFilter === "nonconsumable" ? "app-primary-color button" : "app-primary-color-inverse button"}
                    onClick={() => onClickFilterType("nonconsumable")}
                  >
                    Non-Consumable
                  </Button>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  {showConsumableSection && (
                    <>
                      <Stack direction="horizontal" className="mt-4 mb-2 justify-content-between">
                        <h5 className="app-header-text m-0">Consumable</h5>
                        <IconEmbeddedButton
                          onClickButton={() => onClickAddResource("consumable")}
                          tooltipText="Add"
                          icon={faAdd}
                          disabled={showAddResource}
                        />
                      </Stack>
                      {consumables.length === 0 ? (
                        <p className="mt-2">No Resources</p>
                      ) : (
                        consumables.map((resource: Resource, index: number) => (
                          <OrgResourceCard
                            key={index}
                            resource={resource}
                            getResources={getResources}
                            setOrgToSave={setOrgToSave}
                            setShowToast={setShowToast}
                            setToastText={setToastText}
                            setToastBody={setToastBody}
                            setErrMsg={setErrMsg}
                          />
                        ))
                      )}
                    </>
                  )}
                  {showNonConsumableSection && (
                    <>
                      <Stack direction="horizontal" className="mt-4 mb-2 justify-content-between">
                        <h5 className="app-header-text m-0">Non-Consumable</h5>
                        <IconEmbeddedButton
                          onClickButton={() => onClickAddResource("nonconsumable")}
                          tooltipText="Add"
                          icon={faAdd}
                          disabled={showAddResource}
                        />
                      </Stack>
                      {nonConsumables.length === 0 ? (
                        <p className="mt-2">No Resources</p>
                      ) : (
                        nonConsumables.map((resource: Resource, index: number) => (
                          <OrgResourceCard
                            key={index}
                            resource={resource}
                            getResources={getResources}
                            setOrgToSave={setOrgToSave}
                            setShowToast={setShowToast}
                            setToastText={setToastText}
                            setToastBody={setToastBody}
                            setErrMsg={setErrMsg}
                          />
                        ))
                      )}
                    </>
                  )}
                </Col>
              </Row>
            </>
          )}
          {resources.length === 0 && !showAddResource && (
            <Row className="mt-3">
              <Col xs={6} lg={4}>
                <Button className="app-tertiary-color button" onClick={(() => onClickAddResource())}>
                  Add Resource
                </Button>
              </Col>
            </Row>
          )}
          {showAddResource && (
            <>
              <Row className="align-items-center my-3">
                <Col xs={3}>
                  <SelectObject
                    label="Type"
                    value={resourceType}
                    options={resourceTypes}
                    onUpdate={(e: any) => setResourceType(e.target.value)}
                    required={true}
                    disabled={disableType}
                    hasFocus={disableType ? false : true}
                  />
                </Col>
                <Col xs={6}>
                  <Input
                    label="Name"
                    type="text"
                    value={resourceName}
                    onUpdate={(e: any) => updateName(e)}
                    required={true}
                    hasFocus={disableType ? true : false}
                  />
                </Col>
                <Col xs={3}>
                  <Input
                    label="Quantity"
                    type="number"
                    value={resourceQuantity}
                    onUpdate={(e: any) => updateQuantity(e)}
                    required={true}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12} lg={8}>
                  <Stack direction="horizontal" gap={3}>
                    <Button className="app-secondary-color button" onClick={() => onClickSaveResource(false)} disabled={!allowSaveResource}>
                      Save and Add Another
                    </Button>
                    <div className="vr" />
                    <Button className="app-secondary-color button" onClick={() => onClickSaveResource(true)} disabled={!allowSaveResource}>
                      Save and Done
                    </Button>
                    <div className="vr" />
                    <Button className="app-primary-color button" onClick={() => onClickAddResourceCancel()}>
                      Done
                    </Button>
                  </Stack>
                </Col>
              </Row>
            </>
          )}
        </>
      )}
      <SaveChangesModal showModal={showModal} setShowModal={setShowModal} onLeave={onClickAddResourceCancel} />
    </>
  );
};

export default OrgResources;
